import { ApiError } from '@/api/errors';
import { logger } from '@/lib/logger';
import { captureAppError } from '@/observability/sentry';

/**
 * Centralized error sink. This is the ONLY place render / async errors leave
 * the app for logging. It intentionally takes a narrow, PHI-free shape and
 * never looks at component props / state / fetched data.
 *
 * Swap the sink for Sentry / Datadog later by editing this function — call
 * sites stay stable.
 */

export interface ErrorReport {
  /** Stable source label, e.g. 'error_boundary' or 'session_expired'. */
  readonly source: string;
  /** The raw error. Only safe fields are read off it. */
  readonly error: unknown;
  /**
   * React componentStack from `info.componentStack`. It contains component
   * names only — safe to log. Do NOT include any user-provided text.
   */
  readonly componentStack?: string | null | undefined;
}

function errorName(error: unknown): string {
  if (error instanceof Error) return error.name || 'Error';
  return 'UnknownError';
}

function errorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  return '[non-error thrown]';
}

export function reportError(report: ErrorReport): void {
  const { source, error, componentStack } = report;

  // `errorName` (not `name`) because the logger's PHI redactor strips any
  // field called `name` — including the JS Error class name. Using a distinct
  // key keeps debug context (Error, TypeError, ApiError) while leaving the
  // PHI rule strict.
  const base = {
    source,
    errorName: errorName(error),
    message: errorMessage(error),
  };

  const apiFields =
    error instanceof ApiError
      ? {
          status: error.status,
          code: error.code,
          correlationId: error.correlationId,
        }
      : {};

  const stack =
    typeof componentStack === 'string' && componentStack.length > 0
      ? componentStack.split('\n').slice(0, 12).join('\n')
      : undefined;

  logger.error('app.unhandled_error', {
    ...base,
    ...apiFields,
    ...(stack !== undefined ? { componentStack: stack } : {}),
  });

  // Forward the raw error (for its stack trace + Sentry grouping) with the same
  // PHI-free context. No-op unless Sentry is configured. Kept separate from the
  // logger so the console sink and Sentry never diverge on what's considered safe.
  captureAppError(error, {
    source,
    errorName: base.errorName,
    ...apiFields,
    componentStack: stack,
  });
}
